import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import MainView from "@/views/main/MainView.vue";
import auth from "@/app/auth";
import store from "@/store/index";
import LogoutSuccess from "@/components/shared/LogoutSuccess.vue";
import ErrorComponent from "@/components/shared/Error.vue";

Vue.use(VueRouter);

const routeWhitelist = [
  "name",
  "path",
  "fullPath",
  "hash",
  "matched",
  "components",
  "default",
];

function requireAuth(to: any, from: any, next: any) {
  Vue.prototype.$log.debug(
    `to=${JSON.stringify(to, routeWhitelist, 2)},`,
    `from=${JSON.stringify(from, routeWhitelist, 2)},`,
    `next=${next}`
  );

  if (!auth.auth.isUserSignedIn()) {
    Vue.prototype.$log.debug(
      "user is NOT signed in, setting state to logged out..."
    );
    store.commit("logout");

    Vue.prototype.$log.debug(`signing out...`);
    auth.auth.signOut();
  } else {
    if (!store.state.logged_in) {
      Vue.prototype.$log.debug(
        "state shows us as being logged out, authorizing..."
      );
      auth.handleAuthSuccess().then(() => {
        Vue.prototype.$log.debug("authorized, now routing to next");
        next();
      });
    } else {
      Vue.prototype.$log.debug("routing to next");
      next();
    }
  }
}

const monitorsRoutes = [
  {
    path: "monitors",
    name: "monitor-landing",
    component: () => import("@/views/monitor/MonitorLandingPage.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "monitors/account/:account_id",
    name: "user-monitors",
    component: () => import("@/views/monitor/MonitorList.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "monitors/account/:account_id",
    name: "account-monitors",
    component: () => import("@/views/monitor/MonitorList.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "monitor/config/:monitor_id",
    name: "monitor-details",
    component: () => import("@/views/Map.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "monitor",
    name: "create-monitor",
    component: () => import("@/views/Map.vue"),
    beforeEnter: requireAuth,
  },
];

const baseRoutes = [
  {
    path: "",
    name: "Home",
    component: () => import("@/views/main/HomeLandingPage.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "accounts",
    name: "account-landing",
    component: () => import("@/views/account/AccountsLandingPage.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "accounts/listing",
    name: "account-list",
    component: () => import("@/views/account/AccountList.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "users",
    name: "user-landing",
    component: () => import("@/views/user/UsersLandingPage.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "users/listing",
    name: "user-list",
    component: () => import("@/views/user/UserList.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "accounts/:account_id",
    name: "account-details",
    component: () => import("@/views/account/Account.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "usage/account/:account_id",
    name: "account-usage",
    component: () => import("@/views/usage/AccountUsage.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "users/account/:account_id",
    name: "account-users",
    component: () => import("@/views/user/UserList.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "create_user",
    name: "user-create",
    component: () => import("@/views/user/User.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "admin/account/:account_id/user/:user_id",
    name: "user-details",
    component: () => import("@/views/user/User.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "create_account",
    name: "account-create",
    component: () => import("@/views/account/Account.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "usage/account/:account_id/user/:user_id",
    name: "user-usage",
    component: () => import("@/views/usage/UserUsage.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "orders",
    name: "orders-landing",
    component: () => import("@/views/order/OrdersLandingPage.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "orders/:account_id",
    name: "my-orders",
    component: () => import("@/views/order/OrderList.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "orders/:account_id",
    name: "account-orders",
    component: () => import("@/views/order/OrderList.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "orders/:account_id/order/:order_id",
    name: "order-details",
    component: () => import("@/views/order/Order.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "orders-place",
    name: "create-order",
    component: () => import("@/views/Map.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "select",
    name: "Select",
    component: () => import("@/views/Map.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "selects/:account_id",
    name: "account-selects",
    component: () => import("@/views/select/SelectList.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "credentials/:account_id",
    name: "creds-list",
    component: () => import("@/views/credentials/CredsList.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "credentials/:account_id/:creds_id",
    name: "creds-details",
    component: () => import("@/views/credentials/Creds.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "create_credentials",
    name: "creds-create",
    component: () => import("@/views/credentials/Creds.vue"),
    beforeEnter: requireAuth,
  },

  {
    path: "upgrade-to-latest",
    name: "upgrade-to-latest",
    component: () => import("@/views/UseLatestPipeline.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "upgrade",
    name: "upgrade",
    component: () => import("@/views/UseLatestPipeline.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "login",
    name: "login",
    beforeEnter() {
      Vue.prototype.$log.debug(`login beforeEnter`);

      if (!auth.auth.isUserSignedIn()) {
        Vue.prototype.$log.debug(
          `user is not signed in, calling auth.login()...`
        );
        auth.login();
      }
    },
  },
  {
    path: "login/oauth2/code/cognito",
    name: "login-cognito",
    component: () => import("@/views/main/HomeLandingPage.vue"),
    // beforeEnter(to, from, next) {
    //   const nextPath = sessionStorage.getItem("redirectPath") || "/";
    //   sessionStorage.removeItem("redirectPath");
    //   const currUrl = window.location.href;
    //   auth.auth.parseCognitoWebResponse(currUrl);
    //   next(nextPath);
    // },
  },
  {
    path: "logout",
    name: "logout",
    component: LogoutSuccess,
    beforeEnter(to: any, from: any, next: any) {
      Vue.prototype.$log.debug(
        "logout beforeEnter",
        `to=${JSON.stringify(to, routeWhitelist, 2)},`,
        `from=${JSON.stringify(from, routeWhitelist, 2)},`,
        `next=${next}`
      );
      store.commit("logout");
      auth.logout();
      next();
    },
  },
  {
    path: "error",
    component: ErrorComponent,
  },
  {
    path: "explore",
    name: "Explore",
    component: () => import("@/views/Map.vue"),
    beforeEnter: requireAuth,
  },
];

const routes: Array<RouteConfig> = [
  {
    path: "/",
    component: MainView,
    children: [...baseRoutes, ...monitorsRoutes],
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

export default router;
